import { useState } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { ArrowDownTrayIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { BackgroundTaskStatus, ISavedFilter } from 'shared';

import { Badge } from '../../../../../common/Atoms/Badge';
import { Table } from '../../../../../common/Atoms/Table';
import { Divider } from '../../../../../common/Atoms/Divider';
import { classNames } from '../../../../../common/lib/classNames';
import { useCurrentOrgUsers } from '../../../../hooks/useCurrentOrgUsers';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useFetchSavedFiltersQuery } from '../../../../services/api/savedFiltersApi/savedFilters';
import { useFetchScheduledExportTasksQuery, useLazyFetchExportDownloadUrlQuery } from '../../../../services/api/exportsApi/exports';
import { TwoColFormRow } from '../../../Molecules/Form/TwoColFormRow';
import { SavedFilterSettingsModal } from '../../../Navigation/SavedFilterSettingsModal';
import { describeSchedule } from '../../../Navigation/SavedFilterScheduleFields';

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  [BackgroundTaskStatus.PENDING]: { label: `Scheduled`, color: `blue` },
  [BackgroundTaskStatus.COMPLETE]: { label: `Sent`, color: `green` },
  [BackgroundTaskStatus.FAILED]: { label: `Failed`, color: `red` },
};

function formatDateTime(iso?: string) {
  return iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED) : `-`;
}

function capitalise(value: string) {
  return `${value[0].toUpperCase()}${value.slice(1)}`;
}

// Settings > Advanced > Scheduled Exports: every saved view emailed on a schedule, when it next
// runs, and the recent history of those exports.
export function ScheduledExports() {
  const currentOrg = useGetSelectedOrganisation();
  const organisationId = currentOrg?.id ?? ``;
  const orgUsers = useCurrentOrgUsers(true);
  const [settingsFilter, setSettingsFilter] = useState<ISavedFilter | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [fetchDownloadUrl] = useLazyFetchExportDownloadUrlQuery();

  const { data: filtersData, isLoading: filtersLoading } = useFetchSavedFiltersQuery({ organisationId }, { skip: !organisationId });
  const { data: tasksData, isLoading: tasksLoading } = useFetchScheduledExportTasksQuery({ organisationId }, { skip: !organisationId });

  const scheduledFilters = (filtersData?.savedFilters ?? []).filter(filter => filter.schedule);
  const tasks = tasksData?.exports ?? [];
  const pendingByFilter = new Map(tasks.filter(t => t.status === BackgroundTaskStatus.PENDING).map(t => [t.savedFilterId, t]));
  const history = tasks.filter(t => t.status !== BackgroundTaskStatus.PENDING);

  async function onDownload(id: number) {
    setDownloadingId(id);

    try {
      const { url } = await fetchDownloadUrl({ organisationId, id }).unwrap();

      window.open(url, `_blank`, `noopener noreferrer`);
    }
    catch {
      toast.error(`Could not download the export, it may have expired`);
    }
    finally {
      setDownloadingId(null);
    }
  }

  const scheduleRows = scheduledFilters.map(filter => ({
    name: filter.name,
    type: capitalise(filter.type),
    schedule: describeSchedule(filter.schedule),
    recipients: orgUsers.filter(u => filter.schedule.userIds.includes(String(u.value))).map(u => u.label).join(`, `),
    next: formatDateTime(pendingByFilter.get(filter.id)?.scheduledAt),
    actions: (
      <button
        type={ `button` }
        onClick={ () => setSettingsFilter(filter) }
        title={ `Saved filter settings` }
        className={ `rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700` }
      >
        <Cog6ToothIcon className={ `h-5 w-5` } />
      </button>
    ),
  }));

  const historyRows = history.map(task => {
    const badge = STATUS_BADGE[task.status] ?? { label: task.status, color: `gray` };

    return {
      name: task.filename || `${capitalise(task.entity)} export`,
      type: capitalise(task.entity),
      scheduledAt: formatDateTime(task.scheduledAt ?? task.createdAt),
      status: <Badge color={ badge.color }
        message={ badge.label } />,
      actions: task.downloadable ? (
        <button
          type={ `button` }
          onClick={ () => onDownload(task.id) }
          disabled={ downloadingId === task.id }
          title={ `Download CSV` }
          className={ `rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700` }
        >
          <ArrowDownTrayIcon className={ classNames(`h-5 w-5`, downloadingId === task.id && `animate-pulse`) } />
        </button>
      ) : null,
    };
  });

  return (
    <div className={ `space-y-6` }>
      <TwoColFormRow
        heading={ `Scheduled Exports` }
        description={ `Saved views that are emailed as a CSV on a schedule. Set one up from the cog next to a saved view, or when saving a view.` }
      >
        <Table
          headings={ [
            { name: `name`, label: `View`, textAlign: `left` },
            { name: `type`, label: `Type` },
            { name: `schedule`, label: `Schedule` },
            { name: `recipients`, label: `Send to` },
            { name: `next`, label: `Next email` },
            { name: `actions`, label: `` },
          ] }
          rows={ scheduleRows }
          loading={ filtersLoading || tasksLoading }
          emptyMessage={ `No saved views are scheduled yet.` }
        />
      </TwoColFormRow>

      <Divider weight={ `light` } />

      <TwoColFormRow
        heading={ `History` }
        description={ `Recent scheduled exports. Files can be downloaded until they expire.` }
      >
        <Table
          headings={ [
            { name: `name`, label: `View`, textAlign: `left` },
            { name: `type`, label: `Type` },
            { name: `scheduledAt`, label: `Scheduled for` },
            { name: `status`, label: `Status` },
            { name: `actions`, label: `` },
          ] }
          rows={ historyRows }
          loading={ tasksLoading }
          emptyMessage={ `No scheduled exports have run yet.` }
        />
      </TwoColFormRow>

      <SavedFilterSettingsModal
        filter={ settingsFilter }
        organisationId={ organisationId }
        onClose={ () => setSettingsFilter(null) }
      />
    </div>
  );
}
